/* Zailarah - collection + search listing (<zl-collection>, <zl-collection-trust>).
   Ported from the collection preview script (lines 1598-1870), re-wired to real Shopify data:
   - filtering, sorting and pagination are URLs: every control carries the URL it leads to (data-url / data-url-add /
     data-url-remove / data-next, rendered by Liquid from the storefront filters); this script fetches the section for
     that URL (Section Rendering API), swaps the regions of the page and pushes the URL (history.pushState / popstate);
     without JavaScript the same URLs work as plain links and forms;
   - pills / offer tile switch collection without a reload; "Load more" appends the next page; the filter drawer, view
     toggle, quick add (ZL.cart.add), colour-swatch card swap, bundle tile, free-shipping bar (follows zl:cart),
     trust-row rotation and back-to-top are ported 1:1;
   - after every grid update it emits the document event "zl:collection:rendered" (the reels section re-mounts into
     the grid slot) and "zl:content:replaced" (reveal).
   Plain ES2019, no dependencies. */
(function () {
  'use strict';

  var win = window;
  var doc = document;
  var ZL = (win.ZL = win.ZL || {});

  function $(selector, root) { return (root || doc).querySelector(selector); }
  function $$(selector, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(selector)); }
  function esc(value) {
    if (typeof ZL.esc === 'function') return ZL.esc(value);
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function cssEscape(value) {
    return win.CSS && typeof win.CSS.escape === 'function' ? win.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  }
  function reduced() { return typeof ZL.prefersReducedMotion === 'function' && ZL.prefersReducedMotion(); }
  function safeFocus(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) { /* not focusable */ } }
  }
  /* the element carrying data-fkey `key`, preferring the region (selector) that held focus, else the first VISIBLE one
     (the toolbar price chips and the mobile price chips share the same key; only one row is displayed) */
  function findByKey(root, key, scope) {
    var selector = '[data-fkey="' + cssEscape(key) + '"]';
    var region = scope ? $(scope, root) : null;
    var inRegion = region ? $(selector, region) : null;
    if (inRegion && inRegion.getClientRects().length) return inRegion;
    var all = $$(selector, root);
    var visible = all.filter(function (el) { return el.getClientRects().length > 0; });
    return visible[0] || all[0] || null;
  }
  function adopt(node) { return doc.adoptNode ? doc.adoptNode(node) : node; }
  function moveChildren(from, to) {
    while (to.firstChild) to.removeChild(to.firstChild);
    Array.prototype.slice.call(from.childNodes).forEach(function (node) { to.appendChild(adopt(node)); });
  }
  function plainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }
  function pageUrl() { return win.location.pathname + win.location.search; }
  function store(action, key, value) {
    try {
      if (action === 'get') return win.localStorage.getItem(key);
      win.localStorage.setItem(key, value);
    } catch (e) { /* storage unavailable (private mode) */ }
    return null;
  }

  /* ------------------------------------------------------------------ <zl-collection> */

  class ZlCollection extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sectionId = this.dataset.sectionId;
      this._url = pageUrl();
      this._abort = null;
      this._moreAbort = null;
      this._gen = 0;
      this._panel = null;
      this._cardCfg = undefined;
      this._moreBusy = false;
      this._cardCache = new Map();
      this._countCache = new Map();
      this._countGen = 0;
      this._io = null;
      this._drawerOpener = null;
      this._timers = [];

      this._onClick = this.onClick.bind(this);
      this._onChange = this.onChange.bind(this);
      this._onKeydown = this.onKeydown.bind(this);
      this._onOver = this.onOver.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onPop = this.onPop.bind(this);
      this._onCart = this.onCart.bind(this);
      this._onScroll = this.onScroll.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('mouseover', this._onOver);
      this.addEventListener('focusin', this._onOver);
      doc.addEventListener('click', this._onDocClick);
      win.addEventListener('popstate', this._onPop);
      doc.addEventListener('zl:cart', this._onCart);

      /* the filter drawer starts closed: keep it out of the tab order / accessibility tree */
      var drawer = this.drawer();
      if (drawer) drawer.setAttribute('inert', '');

      /* remembered grid size */
      if (this.dataset.rememberView === 'true') {
        var saved = store('get', 'zlc-view');
        if (saved && /^[1-4]$/.test(saved)) this.setView(saved, true);
      }

      /* free-shipping bar: correct the server state when it cannot know the active currency */
      var ship = $('[data-ship]', this);
      if (ship) {
        var sub = Number(ship.dataset.sub) || 0;
        if (ship.dataset.native === 'false' || ZL.promo && typeof ZL.promo.freeShippingThreshold === 'function' && ZL.promo.freeShippingThreshold() !== Number(ship.dataset.thr)) this.updateShip(sub);
      }

      /* back to top (the button sits right after this element, outside .zlc on purpose) */
      var section = this.closest('.shopify-section') || this.parentNode;
      this.topButton = section ? $('.zlc-top', section) : null;
      if (this.topButton) {
        this._topClick = function () { win.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' }); };
        this.topButton.addEventListener('click', this._topClick);
        win.addEventListener('scroll', this._onScroll, { passive: true });
        this.onScroll();
      }

      this.observeMore();

      /* theme editor: the price preset blocks exist twice (toolbar row + mobile row): keep the block attributes on the
         row that is displayed at the current preview width */
      this._mql = null;
      if (ZL.designMode && win.matchMedia) {
        this._onMql = this.syncPresetAttrs.bind(this);
        this._mql = win.matchMedia('(min-width:1000px)');
        if (typeof this._mql.addEventListener === 'function') this._mql.addEventListener('change', this._onMql);
        else if (typeof this._mql.addListener === 'function') this._mql.addListener(this._onMql);
        this.syncPresetAttrs();
      }

      /* theme editor */
      var self = this;
      this._unsub = typeof ZL.onSection === 'function' ? ZL.onSection(this, {
        deselect: function () { self.closeDrawer(); },
        blockSelect: function (event) { self.onBlockSelect(event); },
        blockDeselect: function (event) {
          if (event.target && event.target.closest && event.target.closest('.zlf-d')) self.closeDrawer();
        }
      }) : null;
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('mouseover', this._onOver);
      this.removeEventListener('focusin', this._onOver);
      doc.removeEventListener('click', this._onDocClick);
      win.removeEventListener('popstate', this._onPop);
      doc.removeEventListener('zl:cart', this._onCart);
      win.removeEventListener('scroll', this._onScroll);
      if (this.topButton && this._topClick) this.topButton.removeEventListener('click', this._topClick);
      if (this._abort) this._abort.abort();
      if (this._moreAbort) this._moreAbort.abort();
      if (this._mql && this._onMql) {
        if (typeof this._mql.removeEventListener === 'function') this._mql.removeEventListener('change', this._onMql);
        else if (typeof this._mql.removeListener === 'function') this._mql.removeListener(this._onMql);
        this._mql = null;
      }
      if (this._io) { this._io.disconnect(); this._io = null; }
      this._timers.forEach(function (id) { clearTimeout(id); });
      this._timers = [];
      if (this._unsub) { this._unsub(); this._unsub = null; }
      if (this._drawerIsOpen) {
        /* removed while the filter drawer is open (e.g. the section re-rendered in the theme editor): free the page */
        if (typeof ZL.unlockScroll === 'function') ZL.unlockScroll('zlc-filter');
        if (this._panel && typeof ZL.releaseFocus === 'function') ZL.releaseFocus(this._panel, { restore: false });
        this._panel = null;
        this._drawerIsOpen = false;
      }
    }

    /* ---------------------------------------------------------------- small accessors */

    grid() { return $('.zlc-grid', this); }
    drawer() { return $('.zlf-d', this); }
    later(fn, ms) {
      var self = this;
      var id = setTimeout(function () {
        var at = self._timers.indexOf(id);
        if (at > -1) self._timers.splice(at, 1);
        fn();
      }, ms);
      this._timers.push(id);
      return id;
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;

      /* links that navigate without a reload */
      var link = target.closest('a[data-col],a[data-goto]');
      if (link && this.contains(link)) {
        var ajax = link.hasAttribute('data-goto') || this.dataset.pillsAjax !== 'false';
        if (ajax && plainClick(event) && /\/collections\//.test(link.getAttribute('href') || '') && !link.target) {
          event.preventDefault();
          this.closeDrawer();
          var href = link.getAttribute('href');
          if (new URL(href, win.location.href).pathname + new URL(href, win.location.href).search !== this._url) {
            this.navigate(href, { collection: true });
          }
        }
        return;
      }
      var pageLink = target.closest('.zlc-more .zl-pagination a[href]');
      if (pageLink && plainClick(event)) {
        event.preventDefault();
        this.navigate(pageLink.getAttribute('href'), { scrollToGrid: true });
        return;
      }

      /* the backdrop is a div, the X and "Show n" are buttons: all of them carry data-fclose */
      var closer = target.closest('[data-fclose]');
      if (closer && this.contains(closer)) { this.closeDrawer(); return; }

      var button = target.closest('button');
      if (!button || !this.contains(button)) return;

      if (button.hasAttribute('data-filter-open')) { this.openDrawer(button); return; }
      if (button.hasAttribute('data-more')) { this.loadMore(); return; }
      if (button.dataset.view) { this.setView(button.dataset.view); return; }
      if (button.hasAttribute('data-qa')) { this.openPicker(button.closest('.zlc-card')); return; }
      if (button.hasAttribute('data-qa-x')) { this.closePicker(button.closest('.zlc-card'), true); return; }
      if (button.dataset.sw) { this.swapCard(button); return; }
      if (button.dataset.bsize) { this.bundleSize(button); return; }
      if (button.dataset.bcol) { this.bundleColour(button); return; }
      if (button.hasAttribute('data-bundle-add')) { this.bundleAdd(button); return; }
      if (button.dataset.vid && button.closest('.zlc-qa')) { this.addToCart(button); return; }
      if (button.dataset.url !== undefined) {
        /* clear / chips / presets / size + colour buttons / empty-state button: they all carry their URL */
        if (button.disabled) return;
        this.navigate(button.dataset.url);
      }
    }

    onChange(event) {
      var target = event.target;
      if (!target || !target.matches) return;
      if (target.matches('select[data-sort]')) {
        var url = new URL(win.location.href);
        url.searchParams.set('sort_by', target.value);
        url.searchParams.delete('page');
        this.navigate(url.pathname + url.search);
        return;
      }
      if (target.matches('input[type="checkbox"][data-url-add]')) {
        this.navigate(target.checked ? target.getAttribute('data-url-add') : target.getAttribute('data-url-remove'));
        return;
      }
      if (target.closest('[data-range]')) this.applyRange(target.closest('[data-range]'));
    }

    onKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      var card = event.target && event.target.closest ? event.target.closest('.zlc-card.is-picking') : null;
      if (card) {
        event.stopPropagation();
        this.closePicker(card, true);
      }
    }

    onOver(event) {
      var swatch = event.target && event.target.closest ? event.target.closest('[data-sw]') : null;
      if (swatch && swatch.getAttribute('aria-pressed') !== 'true') this.fetchCard(swatch.dataset.url || '', true);
    }

    onDocClick(event) {
      /* a click anywhere outside a card closes every open size picker */
      if (event.target && event.target.closest && event.target.closest('.zlc-card')) return;
      $$('.zlc-card.is-picking', this).forEach(function (card) { card.classList.remove('is-picking'); });
    }

    onPop() {
      var now = pageUrl();
      if (now === this._url) return;      /* only the hash changed */
      this.navigate(now, { push: false });
    }

    onCart(event) {
      var detail = (event && event.detail) || {};
      this.updateShip(Number(detail.sub) || 0);
    }

    onScroll() {
      if (!this.topButton) return;
      var offset = Number(this.topButton.dataset.offset) || 1200;
      this.topButton.classList.toggle('is-on', (win.scrollY || win.pageYOffset) > offset);
    }

    onBlockSelect(event) {
      var el = event.target;
      if (!el || !el.matches) return;
      if (el.matches('.zlf-opt__hint')) { this.openDrawer(null, true); return; }
      if (el.matches('.zlc-ugc,.zlc-bundle,.zlc-offer') && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    }

    /* ---------------------------------------------------------------- navigation (filters, sort, pills, pages) */

    setBusy(on) {
      var grid = this.grid();
      if (on) { this.setAttribute('data-loading', ''); if (grid) grid.setAttribute('aria-busy', 'true'); }
      else { this.removeAttribute('data-loading'); if (grid) grid.removeAttribute('aria-busy'); }
    }

    /* Fetches the section for `url`, swaps the regions of the listing and pushes the URL. Any failure falls back to a
       normal page load of that URL. opts: { push:false (popstate), collection:true, scrollToGrid:true } */
    navigate(url, opts) {
      var options = opts || {};
      if (!url) return Promise.resolve();
      var parsed = new URL(url, win.location.href);
      var target = parsed.pathname + parsed.search;
      var self = this;
      if (this._abort) this._abort.abort();
      /* a "load more" still in flight belongs to the view we are leaving: drop it */
      this._gen++;
      if (this._moreAbort) { this._moreAbort.abort(); this._moreAbort = null; }
      this._moreBusy = false;
      var controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      this._abort = controller;
      this.setBusy(true);
      return ZL.fetchHTML(ZL.sectionUrl(target, this.sectionId), controller ? { signal: controller.signal } : undefined).then(function (page) {
        if (self._abort !== controller) return;
        var wrapper = page.getElementById('shopify-section-' + self.sectionId);
        var next = wrapper ? wrapper.querySelector('zl-collection') : null;
        if (!next) throw new Error('section missing');
        self.applyResponse(next, target, options);
        self._abort = null;
        self.setBusy(false);
      }).catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (self._abort === controller || !controller) win.location.href = target;
      });
    }

    /* region swap. The grid, the active chips and the toolbar parts are replaced INSIDE their elements so the open
       drawer, the grid size (data-cols) and the focus survive; the reels node is re-mounted by the reels section. */
    applyResponse(next, url, options) {
      var self = this;
      var changed = next.dataset.handle !== this.dataset.handle;
      var active = doc.activeElement;
      var focusKey = null;
      var focusRegion = '';
      var focusScope = '';
      if (active && active !== doc.body && this.contains(active)) {
        focusKey = active.getAttribute('data-fkey');
        if (!focusKey && active.closest('.zlc-active')) focusRegion = 'active';
        if (focusKey) {
          ['.zlc-presets-m', '.zlc-presets', '[data-fbody]', '.zlc-active'].some(function (selector) {
            if (active.closest(selector)) { focusScope = selector; return true; }
            return false;
          });
        }
      }

      /* drawer: remember which groups are open and where it was scrolled */
      var body = $('[data-fbody]', this);
      var open = {};
      var scrollTop = body ? body.scrollTop : 0;
      if (body) $$('details[data-g]', body).forEach(function (d) { open[d.getAttribute('data-g')] = d.open; });

      var structural = ['.zlc-sticky', '.zlc-grid', '.zlc-more'].some(function (selector) { return Boolean($(selector, self)) !== Boolean($(selector, next)); });
      if (structural) { win.location.href = url; return; }

      ['.zlc-hero', '.zlc-pills', '.zlc-subpills', '.zlc-presets', '.zlc-presets-m', '.zlc-active', '.zlc-count', '[data-fbody]'].forEach(function (selector) {
        var from = $(selector, next);
        var to = $(selector, self);
        if (from && to) moveChildren(from, to);
        else if (to && !from && selector !== '[data-fbody]') moveChildren(doc.createElement('div'), to);
      });
      if (win.ZLCenterPills) win.ZLCenterPills(this, true);
      /* sort select: replace the options, keep the element (and its focus) */
      var selectFrom = $('.zlc-sort select', next);
      var selectTo = $('.zlc-sort select', this);
      if (selectFrom && selectTo) { var sortValue = selectFrom.value; moveChildren(selectFrom, selectTo); selectTo.value = sortValue; }
      /* filter button badge */
      var badgeFrom = $('.zlc-tool b', next);
      var badgeTo = $('.zlc-tool b', this);
      if (badgeFrom && badgeTo) { badgeTo.textContent = badgeFrom.textContent; badgeTo.hidden = badgeFrom.hidden; }
      var showN = $('[data-show-n]', next);
      var showTo = $('[data-show-n]', this);
      if (showN && showTo) showTo.textContent = showN.textContent;
      var clearFrom = $('.zlf-d__foot [data-clear]', next);
      var clearTo = $('.zlf-d__foot [data-clear]', this);
      if (clearFrom && clearTo) clearTo.setAttribute('data-url', clearFrom.getAttribute('data-url') || '');

      /* grid + load more */
      var gridFrom = $('.zlc-grid', next);
      var gridTo = this.grid();
      moveChildren(gridFrom, gridTo);
      var moreFrom = $('.zlc-more', next);
      var moreTo = $('.zlc-more', this);
      moveChildren(moreFrom, moreTo);
      moreTo.hidden = moreFrom.hidden;

      ['data-handle', 'data-title', 'data-page-title', 'data-total'].forEach(function (name) { self.setAttribute(name, next.getAttribute(name) || ''); });

      /* drawer groups: restore open state + scroll */
      var newBody = $('[data-fbody]', this);
      if (newBody) {
        $$('details[data-g]', newBody).forEach(function (d) {
          var key = d.getAttribute('data-g');
          if (Object.prototype.hasOwnProperty.call(open, key)) d.open = open[key];
        });
        newBody.scrollTop = scrollTop;
      }

      /* url, title */
      if (options.push !== false) win.history.pushState({ zlc: true }, '', url);
      this._url = pageUrl();
      if (next.dataset.pageTitle) doc.title = next.dataset.pageTitle;

      /* focus */
      if (focusKey) safeFocus(findByKey(this, focusKey, focusScope));
      else if (focusRegion === 'active' || (active && active !== doc.body && !doc.contains(active))) {
        safeFocus($('.zlc-active button', this) || $('[data-filter-open]', this));
      }

      /* collection switched: scroll rule of the prototype (only when scrolled past the pills) */
      if (options.collection) {
        var pills = $('.zlc-pills', this) || $('.zlc-hero', this);
        if (pills) {
          var top = pills.getBoundingClientRect().top + (win.scrollY || win.pageYOffset) - 140;
          if ((win.scrollY || win.pageYOffset) > top) win.scrollTo({ top: top, behavior: reduced() ? 'auto' : 'smooth' });
        }
      } else if (options.scrollToGrid) {
        var gridTop = gridTo.getBoundingClientRect().top + (win.scrollY || win.pageYOffset) - 140;
        win.scrollTo({ top: Math.max(gridTop, 0), behavior: reduced() ? 'auto' : 'smooth' });
      }

      this._gen++;
      this.syncPresetAttrs();
      this.observeMore();
      this._countCache.clear();
      this._countGen++;
      if (this._drawerIsOpen) this.loadPresetCounts();
      if (changed) this.refreshSeo(url);
      this.announce(gridTo);
    }

    announce(grid) {
      if (typeof ZL.emit !== 'function') return;
      ZL.emit('zl:content:replaced', { root: grid });
      ZL.emit('zl:collection:rendered', {
        section: this,
        sectionId: this.sectionId,
        root: this,
        grid: grid,
        url: this._url,
        append: false,
        collection: { handle: this.dataset.handle, title: this.dataset.title }
      });
    }

    /* the SEO text section may show per-collection text: refetch it when the collection changed */
    refreshSeo(url) {
      $$('[data-zlc-seo-dynamic]').forEach(function (el) {
        var id = el.getAttribute('data-section-id');
        if (!id) return;
        ZL.fetchSection(url, id).then(function (wrapper) {
          var fresh = wrapper ? $('.zlc', wrapper) : null;
          if (fresh && el.isConnected) moveChildren(fresh, el);
        });
      });
    }

    applyRange(box) {
      var min = $('[data-range-min]', box);
      var max = $('[data-range-max]', box);
      var base = box.getAttribute('data-url-base') || '';
      var parts = [];
      if (min && min.value !== '') parts.push(box.getAttribute('data-min-param') + '=' + encodeURIComponent(min.value));
      if (max && max.value !== '') parts.push(box.getAttribute('data-max-param') + '=' + encodeURIComponent(max.value));
      this.navigate(parts.length ? base + (base.indexOf('?') > -1 ? '&' : '?') + parts.join('&') : base);
    }

    /* ---------------------------------------------------------------- load more / infinite */

    loadMore() {
      var button = $('[data-more]', this);
      var nextUrl = button ? button.getAttribute('data-next') : '';
      if (!button || !nextUrl || this._moreBusy || this._abort) return;
      var self = this;
      var generation = this._gen;
      var controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      this._moreAbort = controller;
      this._moreBusy = true;
      button.setAttribute('aria-busy', 'true');
      ZL.fetchHTML(ZL.sectionUrl(nextUrl, this.sectionId), controller ? { signal: controller.signal } : undefined).then(function (page) {
        if (generation !== self._gen) return;      /* a filter / sort / page change happened meanwhile */
        var wrapper = page.getElementById('shopify-section-' + self.sectionId);
        var next = wrapper ? wrapper.querySelector('zl-collection') : null;
        var gridFrom = next ? $('.zlc-grid', next) : null;
        if (!gridFrom) throw new Error('section missing');
        var grid = self.grid();
        var fresh = [];
        Array.prototype.slice.call(gridFrom.children).forEach(function (child) {
          if (child.hasAttribute('data-zl-reels-slot') || child.classList.contains('zlc-empty')) return;
          fresh.push(grid.appendChild(adopt(child)));
        });
        var moreTo = $('.zlc-more', self);
        var moreFrom = $('.zlc-more', next);
        moveChildren(moreFrom, moreTo);
        moreTo.hidden = moreFrom.hidden;
        self._moreAbort = null;
        self._moreBusy = false;
        self.observeMore();
        var first = fresh.length ? ($('.zlc-card__t', fresh[0]) || fresh[0]) : null;
        if (first && first.tagName === 'A') safeFocus(first);
        if (typeof ZL.emit === 'function') {
          ZL.emit('zl:content:replaced', { root: grid });
          ZL.emit('zl:collection:rendered', {
            section: self, sectionId: self.sectionId, root: self, grid: grid, url: self._url, append: true,
            collection: { handle: self.dataset.handle, title: self.dataset.title }
          });
        }
      }).catch(function (error) {
        if (generation !== self._gen || (error && error.name === 'AbortError')) return;
        self._moreAbort = null;
        self._moreBusy = false;
        win.location.href = nextUrl;
      });
    }

    observeMore() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this.dataset.pagination !== 'infinite' || !('IntersectionObserver' in win)) return;
      var button = $('[data-more]', this);
      if (!button || button.hidden) return;
      var self = this;
      this._io = new win.IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) self.loadMore();
      }, { rootMargin: '0px 0px 600px 0px' });
      this._io.observe(button);
    }

    /* ---------------------------------------------------------------- grid size */

    setView(value, silent) {
      var grid = this.grid();
      if (!grid) return;
      grid.dataset.cols = value;
      grid.removeAttribute('data-d3');
      $$('[data-view]', this).forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.view === value ? 'true' : 'false'); });
      if (!silent && this.dataset.rememberView === 'true') store('set', 'zlc-view', value);
    }

    /* ---------------------------------------------------------------- filter drawer */

    openDrawer(opener, keepFocus) {
      var drawer = this.drawer();
      if (!drawer || this._drawerIsOpen) return;
      this._drawerIsOpen = true;
      this._drawerOpener = opener || $('[data-filter-open]', this);
      drawer.removeAttribute('inert');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (this._drawerOpener) this._drawerOpener.setAttribute('aria-expanded', 'true');
      if (typeof ZL.lockScroll === 'function') ZL.lockScroll('zlc-filter');
      var self = this;
      var panel = $('.zlf-d__panel', drawer);
      this._panel = panel;
      if (typeof ZL.trapFocus === 'function' && panel) {
        ZL.trapFocus(panel, { initialFocus: false, returnFocus: this._drawerOpener, onEscape: function () { self.closeDrawer(); } });
      }
      if (!keepFocus) {
        this.later(function () {
          var close = $('.zlf-d__head [data-fclose]', drawer);
          if (close && self._drawerIsOpen) safeFocus(close);
        }, 50);
      }
      this.loadPresetCounts();
    }

    closeDrawer() {
      var drawer = this.drawer();
      if (!drawer || !this._drawerIsOpen) return;
      this._drawerIsOpen = false;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      if (this._drawerOpener) this._drawerOpener.setAttribute('aria-expanded', 'false');
      if (typeof ZL.releaseFocus === 'function') ZL.releaseFocus(this._panel || $('.zlf-d__panel', drawer));
      this._panel = null;
      if (typeof ZL.unlockScroll === 'function') ZL.unlockScroll('zlc-filter');
    }

    /* counts next to the price presets: Shopify gives none, so one small request per preset, only while the drawer is open.
       The request asks for a page far beyond the last one: the section then renders no product cards at all, only the
       header / filters and the total (data-total = the number of matching products). */
    countUrl(url) {
      var parsed = new URL(url, win.location.href);
      parsed.searchParams.set('page', '9999');
      return parsed.pathname + parsed.search;
    }


    loadPresetCounts() {
      if (this.dataset.presetCounts !== 'true') return;
      var self = this;
      var generation = this._countGen;
      var total = Number(this.dataset.total) || 0;
      $$('[data-preset-count]', this).forEach(function (small) {
        var label = small.closest('.zlf-opt');
        var input = label ? $('input', label) : null;
        var set = function (count) {
          small.textContent = String(count);
          if (label) label.classList.toggle('is-off', count === 0 && !(input && input.checked));
        };
        if (input && input.checked) { set(total); return; }
        var url = small.getAttribute('data-preset-url');
        if (!url) return;
        if (self._countCache.has(url)) { set(self._countCache.get(url)); return; }
        ZL.fetchHTML(ZL.sectionUrl(self.countUrl(url), self.sectionId)).then(function (page) {
          var wrapper = page.getElementById('shopify-section-' + self.sectionId);
          var root = wrapper ? wrapper.querySelector('zl-collection') : null;
          if (!root || generation !== self._countGen) return;
          var count = Number(root.dataset.total) || 0;
          self._countCache.set(url, count);
          if (small.isConnected) set(count);
        }).catch(function () { /* the count is optional */ });
      });
    }

    /* ---------------------------------------------------------------- product cards */

    openPicker(card) {
      if (!card) return;
      $$('.zlc-card.is-picking', this).forEach(function (other) { other.classList.remove('is-picking'); });
      card.classList.add('is-picking');
      var first = $('.zlc-qa__sizes button:not([disabled])', card);
      if (first) safeFocus(first);
    }

    closePicker(card, returnFocus) {
      if (!card) return;
      card.classList.remove('is-picking');
      if (returnFocus) safeFocus($('[data-qa]', card));
    }

    addToCart(button) {
      var card = button.closest('.zlc-card');
      var id = Number(button.dataset.vid);
      if (!id || button.getAttribute('aria-busy') === 'true') return;
      var self = this;
      var ms = Number(this.dataset.addedMs) || 1600;
      button.setAttribute('aria-busy', 'true');
      var request;
      if (ZL.cart && typeof ZL.cart.add === 'function') request = ZL.cart.add([{ id: id, quantity: 1 }]);
      else request = this.formAdd([{ id: id, quantity: 1 }]);
      Promise.resolve(request).then(function (result) {
        button.removeAttribute('aria-busy');
        if (result && result.ok === false) {
          /* sold out meanwhile: the cart unit shows the message; mark the size unavailable */
          if (result.status === 422 && button.closest('.zlc-qa__sizes')) button.disabled = true;
          return;
        }
        if (card) {
          card.classList.remove('is-picking');
          card.classList.add('is-added');
          self.later(function () { card.classList.remove('is-added'); }, ms);
        }
      }).catch(function () { button.removeAttribute('aria-busy'); });
    }

    /* without ZL.cart (should not happen): a native form post to /cart/add */
    formAdd(items) {
      var form = doc.createElement('form');
      form.method = 'post';
      form.action = (ZL.routes && ZL.routes.cartAdd) || '/cart/add';
      items.forEach(function (item, i) {
        [['id', item.id], ['quantity', item.quantity]].forEach(function (pair) {
          var input = doc.createElement('input');
          input.type = 'hidden';
          input.name = items.length > 1 ? 'items[' + i + '][' + pair[0] + ']' : pair[0];
          input.value = pair[1];
          form.appendChild(input);
        });
      });
      doc.body.appendChild(form);
      form.submit();
      return { ok: true };
    }

    /* colour swatch: replace the card by the sibling product's card (section helper zl-product-card) */
    fetchCard(url, quiet) {
      if (!url) return Promise.reject(new Error('no url'));
      var key = url.split('?')[0];
      if (!this._cardCache.has(key)) {
        var request = ZL.fetchHTML(ZL.sectionUrl(key, 'zl-product-card')).then(function (page) {
          var card = $('.zlc-card', page);
          if (!card) throw new Error('card missing');
          return card.outerHTML;
        });
        this._cardCache.set(key, request);
        request.catch(function () { /* retried on the next click */ });
        var cache = this._cardCache;
        request.catch(function () { cache.delete(key); });
      }
      var out = this._cardCache.get(key);
      return quiet ? out.catch(function () { return null; }) : out;
    }

    /* the card settings of THIS listing (JSON island printed by zlc-view) */
    cardConfig() {
      if (this._cardCfg !== undefined) return this._cardCfg;
      var node = $('script[data-card-config]', this);
      var cfg = null;
      if (node) { try { cfg = JSON.parse(node.textContent); } catch (e) { cfg = null; } }
      this._cardCfg = cfg;
      return cfg;
    }

    /* The helper section prints a card with every optional element and data-zlc-* markers on the texts: apply the toggles,
       labels and limits of the page the card is swapped into, so it equals its neighbours. */
    conformCard(card) {
      var cfg = this.cardConfig();
      if (!card || !cfg) return card;
      var drop = function (el) { if (el && el.parentNode) el.parentNode.removeChild(el); };
      if (!cfg.second) $$('.zlc-card__media img.alt', card).forEach(drop);
      if (!cfg.badges) drop($('.zlc-badges', card));
      var pct = card.getAttribute('data-pct') || '0';
      var saleText = String(cfg.saleText).replace(/\[pct\]/g, pct);
      $$('[data-zlc-sale]', card).forEach(function (el) { el.textContent = saleText; });
      if (!cfg.rating) drop($('.zlc-stars', card));
      var sw = $('.zlc-sw', card);
      if (sw) {
        var total = Number(sw.getAttribute('data-total')) || 0;
        var max = Number(cfg.maxSw) || 5;
        $$('button', sw).forEach(function (b, i) { if (i >= max) drop(b); });
        $$('span', sw).forEach(drop);
        if (total > max) {
          var more = doc.createElement('span');
          more.textContent = '+' + (total - max);
          sw.appendChild(more);
        }
      }
      if (!cfg.sizes) $$('[data-zlc-sizes]', card).forEach(drop);
      $$('[data-zlc-only]', card).forEach(function (el) { el.textContent = String(cfg.onlyLeft).replace(/\[size\]/g, el.getAttribute('data-zlc-only') || ''); });
      $$('[data-zlc-few]', card).forEach(function (el) { el.textContent = cfg.fewLeft; });
      $$('[data-zlc-colours]', card).forEach(function (el) { el.textContent = String(cfg.colours).replace(/\[n\]/g, el.getAttribute('data-zlc-colours') || '0'); });
      $$('[data-zlc-soldout]', card).forEach(function (el) { el.textContent = cfg.soldOut; });
      var notify = $('[data-zlc-notify]', card);
      if (notify) {
        if (!cfg.notify) drop(notify);
        else {
          notify.textContent = cfg.notifyText;
          notify.setAttribute('href', (notify.getAttribute('data-zlc-notify') || '') + cfg.notifyAnchor);
        }
      }
      if (!cfg.quickAdd) drop($('.zlc-qa', card));
      $$('[data-zlc-qa]', card).forEach(function (el) {
        el.textContent = '\u00a0' + (el.getAttribute('data-zlc-qa') === 'bag' ? cfg.addBagText : cfg.quickAddText);
      });
      return card;
    }

    /* theme editor only: price preset blocks live in two rows; the block attributes go to the visible row */
    syncPresetAttrs() {
      if (!ZL.designMode) return;
      var desk = $('.zlc-presets', this);
      var mob = $('.zlc-presets-m', this);
      if (!desk || !mob) return;
      var mobileView = win.matchMedia ? !win.matchMedia('(min-width:1000px)').matches : false;
      var from = mobileView ? desk : mob;
      var to = mobileView ? mob : desk;
      $$('[data-preset]', from).forEach(function (chip) {
        var twin = $('[data-preset="' + cssEscape(chip.getAttribute('data-preset')) + '"]', to);
        if (!twin) return;
        Array.prototype.slice.call(chip.attributes).forEach(function (attr) {
          if (attr.name.indexOf('data-shopify') !== 0) return;
          twin.setAttribute(attr.name, attr.value);
          chip.removeAttribute(attr.name);
        });
      });
    }

    swapCard(button) {
      var card = button.closest('.zlc-card');
      var url = button.dataset.url || '';
      if (!card || button.getAttribute('aria-pressed') === 'true') return;
      if (!url) return;
      var handle = button.dataset.sw;
      var self = this;
      this.fetchCard(url).then(function (html) {
        if (!card.isConnected) return;
        var holder = doc.createElement('div');
        holder.innerHTML = html;
        var fresh = holder.firstElementChild;
        if (!fresh) return;
        self.conformCard(fresh);
        card.replaceWith(fresh);
        safeFocus($('[data-sw="' + cssEscape(handle) + '"]', fresh) || $('[data-sw][aria-pressed="true"]', fresh));
        if (typeof ZL.emit === 'function') ZL.emit('zl:content:replaced', { root: fresh });
      }).catch(function () { win.location.href = url; });
    }

    /* ---------------------------------------------------------------- bundle tile */

    bundleData(root) {
      var node = root ? $('script[data-bundle-data]', root) : null;
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    }

    bundleSize(button) {
      var item = button.closest('[data-bitem]');
      if (!item) return;
      $$('[data-bsize]', item).forEach(function (b) { b.setAttribute('aria-checked', b === button ? 'true' : 'false'); });
      item.dataset.vid = button.dataset.bsize;
    }

    /* switch the colour of one bundle item: keep the same size when the new colour has it, else the first available */
    bundleColour(button) {
      var root = button.closest('[data-bundle]');
      var data = this.bundleData(root);
      if (!root || !data) return;
      var items = $$('[data-bitem]', root);
      var index = Number(button.closest('[data-bitem]').dataset.bitem);
      var handles = items.map(function (item) { return item.dataset.h; });
      var vids = items.map(function (item) { return Number(item.dataset.vid); });
      var sibs = data.slots[index] || [];
      var current = sibs.filter(function (s) { return s.h === handles[index]; })[0];
      var currentSize = '';
      if (current) current.v.forEach(function (v) { if (v[0] === vids[index]) currentSize = v[1]; });
      var chosen = sibs.filter(function (s) { return s.h === button.dataset.bcol; })[0];
      if (!chosen) return;
      var same = chosen.v.filter(function (v) { return v[1] === currentSize && v[2]; })[0];
      handles[index] = chosen.h;
      vids[index] = same ? same[0] : 0;
      var html = this.bundleHTML(data, handles, vids);
      if (!html) return;
      var holder = doc.createElement('div');
      holder.innerHTML = html;
      var fresh = holder.firstElementChild;
      fresh.appendChild(adopt(root.querySelector('script[data-bundle-data]').cloneNode(true)));
      Array.prototype.slice.call(root.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-shopify') === 0) fresh.setAttribute(attr.name, attr.value);
      });
      root.replaceWith(fresh);
      var checked = $('[data-bitem="' + index + '"] [data-bcol][aria-checked="true"]', fresh);
      if (checked) safeFocus(checked);
    }

    /* the markup of snippets/zlc-tile-bundle.liquid (keep in sync) */
    bundleHTML(data, handles, vids) {
      var picks = handles.map(function (h, i) {
        var sibs = data.slots[i] || [];
        return sibs.filter(function (s) { return s.h === h; })[0] || null;
      });
      if (picks.some(function (p) { return !p; }) || picks.some(function (p) { return !p.a; })) return '';
      var total = 0;
      var was = 0;
      var parts = picks.map(function (p, i) {
        var sel = p.v.filter(function (v) { return v[0] === vids[i] && v[2]; })[0] || p.v.filter(function (v) { return v[2]; })[0];
        if (!sel) return '';
        vids[i] = sel[0];
        total += p.n;
        was += p.w || p.n;
        var sibs = data.slots[i] || [];
        var label = data.labels[i] || '';
        var imgs = '<img src="' + esc(p.i[0] || '') + '" alt="' + esc(p.t) + '" loading="lazy">' + (p.i[1] ? '<img class="alt" src="' + esc(p.i[1]) + '" alt="" loading="lazy">' : '');
        var cols = '';
        if (data.showColours && sibs.length > 1) {
          cols = '<div class="zlc-bundle__cols" role="radiogroup" aria-label="' + esc(String(data.colourAria).replace('[label]', label)) + '"><span>' + esc(p.c) + '</span>' + sibs.map(function (x) {
            return '<button type="button" role="radio" style="--c:' + esc(x.x) + '" data-bcol="' + esc(x.h) + '" aria-checked="' + (x.h === p.h ? 'true' : 'false') + '" aria-label="' + esc(x.c + (x.a ? '' : data.soldOut)) + '"' + (x.a ? '' : ' disabled') + '></button>';
          }).join('') + '</div>';
        }
        var sizes = '';
        if (data.showSizes) {
          sizes = '<div class="zlc-bundle__sizes" role="radiogroup" aria-label="' + esc(String(data.sizeAria).replace('[label]', label)) + '">' + p.v.map(function (v) {
            return '<button type="button" role="radio" data-bsize="' + v[0] + '" aria-checked="' + (v[0] === sel[0] ? 'true' : 'false') + '"' + (v[2] ? '' : ' disabled') + '>' + esc(v[1]) + '</button>';
          }).join('') + '</div>';
        }
        return '<div class="zlc-bundle__item" data-bitem="' + i + '" data-vid="' + sel[0] + '" data-h="' + esc(p.h) + '"><a class="zlc-bundle__img" href="' + esc(p.u) + '">' + imgs + '</a>' +
          '<span class="zlc-bundle__tag">' + esc(label) + ' · ' + esc(ZL.money(p.n)) + '</span><div class="zlc-bundle__opts">' + cols + sizes + '</div></div>';
      });
      if (parts.some(function (part) { return !part; })) return '';
      var kicker = data.kicker || '';
      if (kicker && picks[0].c && picks[1].c) kicker += ' · ' + picks[0].c + ' + ' + picks[1].c;
      var title = data.title || (picks[0].f + ' + ' + picks[1].f);
      return '<div class="zlc-bundle" data-bundle><div class="zlc-bundle__imgs">' + parts.join('<span class="zlc-bundle__plus" aria-hidden="true">+</span>') + '</div>' +
        '<div class="zlc-bundle__info"><div class="zlc-bundle__txt">' + (kicker ? '<span class="zlc-bundle__k">' + esc(kicker) + '</span>' : '') + '<span class="zlc-bundle__t">' + esc(title) + '</span></div>' +
        '<div class="zlc-bundle__buy"><span class="zlc-bundle__p">' + esc(ZL.money(total)) + (total < was ? '<s>' + esc(ZL.money(was)) + '</s>' : '') + '</span><button type="button" class="zl-btn zl-btn--solid zlc-bundle__btn" data-bundle-add>' + esc(data.button) + '</button></div></div></div>';
    }

    bundleAdd(button) {
      var root = button.closest('[data-bundle]');
      if (!root || button.getAttribute('aria-busy') === 'true') return;
      var items = $$('[data-bitem]', root).map(function (item) { return { id: Number(item.dataset.vid), quantity: 1 }; });
      if (items.some(function (item) { return !item.id; })) return;
      button.setAttribute('aria-busy', 'true');
      var request = ZL.cart && typeof ZL.cart.add === 'function' ? ZL.cart.add(items) : this.formAdd(items);
      Promise.resolve(request).then(function () { button.removeAttribute('aria-busy'); }, function () { button.removeAttribute('aria-busy'); });
    }

    /* ---------------------------------------------------------------- free-shipping bar (follows the cart) */

    updateShip(sub) {
      var el = $('[data-ship]', this);
      if (!el) return;
      var threshold = ZL.promo && typeof ZL.promo.freeShippingThreshold === 'function' ? ZL.promo.freeShippingThreshold() : Number(el.dataset.thr) || 0;
      if (!threshold) return;
      var gap = threshold - sub;
      var text;
      if (gap <= 0) text = el.getAttribute('data-t-done') || '';
      else if (sub > 0) text = (el.getAttribute('data-t-progress') || '').replace(/\[amount\]/g, ZL.money(gap));
      else text = (el.getAttribute('data-t-empty') || '').replace(/\[amount\]/g, typeof ZL.promo.freeShippingLabel === 'function' ? ZL.promo.freeShippingLabel() : ZL.moneyShort(threshold));
      el.classList.toggle('is-done', gap <= 0);
      var label = $('.zlc-ship__t', el);
      /* the texts are merchant-written inline HTML (bold allowed), exactly like the server render */
      if (label && text) label.innerHTML = text;
      var bar = $('.zlc-ship__bar i', el);
      if (bar) bar.style.width = Math.min(sub / threshold, 1) * 100 + '%';
    }
  }

  /* ------------------------------------------------------------------ <zl-collection-trust> (rotating trust row) */

  class ZlCollectionTrust extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var self = this;
      this._items = $$('.zlc-trust li', this);
      this._index = 0;
      this._timer = null;
      this._paused = false;
      this._onVisibility = function () { if (doc.hidden) self.stop(); else self.start(); };
      this._onEnter = function () { self._paused = true; };
      this._onLeave = function () { self._paused = false; };
      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusin', this._onEnter);
      this.addEventListener('focusout', this._onLeave);
      doc.addEventListener('visibilitychange', this._onVisibility);
      this.start();
      this._unsub = typeof ZL.onSection === 'function' ? ZL.onSection(this, {
        blockSelect: function (event) {
          var li = event.target && event.target.closest ? event.target.closest('li') : null;
          var at = li ? self._items.indexOf(li) : -1;
          if (at < 0) return;
          self.stop();
          self.show(at);
        },
        blockDeselect: function () { self.start(); }
      }) : null;
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.stop();
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      this.removeEventListener('focusin', this._onEnter);
      this.removeEventListener('focusout', this._onLeave);
      doc.removeEventListener('visibilitychange', this._onVisibility);
      if (this._unsub) { this._unsub(); this._unsub = null; }
    }

    start() {
      this.stop();
      if (this.dataset.rotate === 'false' || this._items.length < 2) return;
      var self = this;
      this._timer = setInterval(function () { self.tick(); }, Number(this.dataset.interval) || 2000);
    }

    stop() {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    }

    /* below 1000px one item at a time: the current one slides out, the next slides in (prototype) */
    tick() {
      if (win.innerWidth >= 1000 || this._paused) return;
      var items = this._items;
      var previous = this._index;
      this._index = (this._index + 1) % items.length;
      items[previous].classList.remove('is-on');
      items[previous].classList.add('is-out');
      items[this._index].classList.remove('is-out');
      items[this._index].classList.add('is-on');
      setTimeout(function () { items[previous].classList.remove('is-out'); }, 500);
    }

    show(at) {
      this._items.forEach(function (li, i) {
        li.classList.remove('is-out');
        li.classList.toggle('is-on', i === at);
      });
      this._index = at;
    }
  }

  if (!customElements.get('zl-collection')) customElements.define('zl-collection', ZlCollection);
  if (!customElements.get('zl-collection-trust')) customElements.define('zl-collection-trust', ZlCollectionTrust);
})();

/* category pills: keep the selected pill in view (centred) instead of jumping back to the start of the row */
(function () {
  'use strict';
  /* a row the shopper has swiped is theirs: the late "load" pass must not snap it back */
  function mark(e) {
    var row = e.target && e.target.closest ? e.target.closest('.zlc-pills,.zlc-subpills') : null;
    if (row) row.setAttribute('data-touched', '');
  }
  ['touchstart', 'pointerdown', 'wheel'].forEach(function (t) { document.addEventListener(t, mark, { passive: true, capture: true }); });
  function centre(root, force) {
    var scope = root && root.querySelectorAll ? root : document;
    ['.zlc-pills', '.zlc-subpills'].forEach(function (sel) {
      [].forEach.call(scope.querySelectorAll(sel), function (row) {
        if (!force && row.hasAttribute('data-touched')) return;
        row.removeAttribute('data-touched');
        var on = row.querySelector('[aria-current="page"]') || row.querySelector('[aria-current="true"]');
        if (!on || row.scrollWidth <= row.clientWidth) return;
        var r = row.getBoundingClientRect(), p = on.getBoundingClientRect();
        row.scrollLeft = Math.max(0, row.scrollLeft + (p.left - r.left) - (row.clientWidth - p.width) / 2);
      });
    });
  }
  window.ZLCenterPills = centre;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { centre(document); });
  else centre(document);
  window.addEventListener('load', function () { centre(document); });
  window.addEventListener('popstate', function () { setTimeout(function () { centre(document); }, 60); });
})();
